import { Stack, Typography } from '@mui/material';
import cx from 'classnames';
import MarkdownEditor from 'new/components/LexicalMarkdownEditor';
import { MarkdownDiffPanel } from 'new/elements/MarkdownDiffView';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import { ModalOutProps } from 'new/routes/ModalDefault';
import React, { useEffect, useMemo, useState } from 'react';
import classes from './ModalCompare.module.scss';

interface ModalCompareProps {
  original: string;
  modified: string;
  onSave: (modification: string) => void;
  onCancel: () => void;
}

export const ModalCompare = (props: ModalCompareProps & ModalOutProps) => {
  const { original = '', modified = '', setModalActions, setFullWidth, onSave } = props;

  const [modifiedSentences, setModifiedSentences] = useState(modified);
  const [showEditor, setShowEditor] = useState(false);

  // Widen the dialog so the side-by-side Before/After diff has room (otherwise
  // the rich-text panes wrap badly and long words clip).
  useEffect(() => {
    setFullWidth?.(true);
  }, [setFullWidth]);

  useEffect(() => {
    setModalActions({
      ok: {
        text: 'Accept Changes',
        onClick: () => onSave(modifiedSentences),
      },
    });
  }, [modifiedSentences]);

  const onChangeSentences = useDebouncedCallback(
    (value) => {
      setModifiedSentences(value);
    },
    [],
    500,
  );

  const onClickEdit = () => {
    setShowEditor(true);
  };

  const committedValue = useMemo(() => {
    return { value: modifiedSentences };
  }, [showEditor]);

  return (
    <Stack height="500px" width="100%" direction="row" gap="16px" sx={{ overflow: 'hidden' }}>
      <div className={classes.sentencesWrapper}>
        <Typography variant="subtitle2">Before</Typography>
        <div className={classes.sentencesContent}>
          <MarkdownDiffPanel
            content={original}
            original={original}
            modified={modifiedSentences}
            highlight="removed"
          />
        </div>
      </div>
      <div className={classes.sentencesWrapper}>
        <Typography variant="subtitle2">After</Typography>
        {showEditor ? (
          // chromeless so the editor fills this bounded pane and scrolls its
          // content internally (default mode has no scroll region and clips
          // tall content like tables). Edits flow live to "Accept Changes" via
          // onChange, so there's no separate "Done" affordance.
          <div className={cx(classes.sentencesContent, classes.editorFill)}>
            <MarkdownEditor
              committedValue={committedValue}
              onChange={onChangeSentences}
              chromeless
            />
          </div>
        ) : (
          <div
            onClick={onClickEdit}
            className={cx(classes.sentencesContent, classes.sentencesContent_clickable)}
          >
            <MarkdownDiffPanel
              content={modifiedSentences}
              original={original}
              modified={modifiedSentences}
              highlight="added"
            />
            <div className={classes.clickToEdit}>Click to edit</div>
          </div>
        )}
      </div>
    </Stack>
  );
};
